import { Controller } from "@hotwired/stimulus"
import confetti from "canvas-confetti"

export default class extends Controller {
  static targets = []
  
  handleSubmit(event) {
    // Only trigger celebration for new time log creation, not edits
    if (this.element.action.includes('/time_logs/') && !this.element.action.endsWith('/time_logs')) {
      // This is an edit form, let it submit normally
      return
    }
    
    // Prevent default submission for new time logs
    event.preventDefault()
    
    // Get form data
    const form = event.target
    const formData = new FormData(form)
    
    // Submit form via AJAX to check validation first
    this.submitFormWithValidation(form, formData)
  }
  
  async submitFormWithValidation(form, formData) {
    try {
      // Show loading state
      this.setFormLoading(form, true)
      
      // Get CSRF token
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content ||
                       document.querySelector('[name="csrf-token"]')?.content ||
                       form.querySelector('[name="authenticity_token"]')?.value
      
      // Add CSRF token to form data if not already present
      if (csrfToken && !formData.has('authenticity_token')) {
        formData.append('authenticity_token', csrfToken)
      }
      
      // Submit form via fetch
      const response = await fetch(form.action, {
        method: form.method || 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': csrfToken
        },
        credentials: 'same-origin'
      })
      
      if (response.ok && (response.redirected || response.status === 200)) {
        // Success - validation passed
        
        // Check if celebrations are enabled before showing
        const celebrationsEnabled = await this.isCelebrationsEnabled()
        
        if (celebrationsEnabled) {
          // Extract celebration data from form
          const categoryInfo = this.getCategoryInfoFromForm(formData)
          const duration = this.calculateDurationFromForm(formData)
          
          // Show celebration
          this.showCelebration(categoryInfo, duration)
          
          // Navigate to success page after celebration
          setTimeout(() => {
            if (response.redirected) {
              window.location.href = response.url
            } else {
              // Fallback: redirect to time logs index
              window.location.href = '/time_logs'
            }
          }, 2500) // 2.5 second delay to let celebration play
        } else {
          // Skip celebration and redirect immediately
          if (response.redirected) {
            window.location.href = response.url
          } else {
            window.location.href = '/time_logs'
          }
        }
      } else if (response.status === 422) {
        // Validation failed - update form with errors
        const responseText = await response.text()
        console.log('422 Response received, processing validation errors...')
        console.log('Response is partial form content, replacing current form...')
        
        // For partial responses, replace the entire form element
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = responseText
        
        // The response should contain the form partial
        const newFormContent = tempDiv.querySelector('form')
        if (newFormContent) {
          console.log('Found form in partial response, replacing current form...')
          // Replace the current form completely
          form.outerHTML = newFormContent.outerHTML
          
          // Re-initialize Stimulus controllers on the new form
          if (window.Stimulus) {
            window.Stimulus.start()
          }
        } else {
          console.log('No form found in partial, response might be just the form content...')
          // Response might be just the form content without form tag
          // Replace the form's inner content
          form.innerHTML = responseText
          
          // Re-initialize Stimulus controllers
          if (window.Stimulus) {
            window.Stimulus.start()
          }
        }
        
        this.setFormLoading(form, false)
      } else {
        // Other error - fall back to normal form submission
        console.warn('Unexpected response status:', response.status)
        this.setFormLoading(form, false)
        form.submit()
      }
    } catch (error) {
      console.error('Form submission error:', error)
      this.setFormLoading(form, false)
      
      // Fallback: submit form normally
      form.submit()
    }
  }
  
  setFormLoading(form, loading) {
    const submitButton = form.querySelector('input[type="submit"], button[type="submit"]')
    if (submitButton) {
      if (loading) {
        submitButton.disabled = true
        submitButton.classList.add('opacity-50', 'cursor-not-allowed')
        const originalText = submitButton.value || submitButton.textContent
        submitButton.dataset.originalText = originalText
        if (submitButton.tagName === 'INPUT') {
          submitButton.value = 'Creating...'
        } else {
          submitButton.textContent = 'Creating...'
        }
      } else {
        submitButton.disabled = false
        submitButton.classList.remove('opacity-50', 'cursor-not-allowed')
        const originalText = submitButton.dataset.originalText
        if (originalText) {
          if (submitButton.tagName === 'INPUT') {
            submitButton.value = originalText
          } else {
            submitButton.textContent = originalText
          }
        }
      }
    }
  }
  
  getCategoryInfoFromForm(formData) {
    const categoryId = formData.get('time_log[category_id]')
    
    // Get category colors (same as timer controller)
    const categories = {
      colors: ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#06B6D4", "#F97316", "#84CC16", "#EC4899", "#6B7280"]
    }
    
    // Get category name from the searchable dropdown
    const categoryInput = document.querySelector('[data-searchable-dropdown-target="searchInput"]')
    const categoryName = categoryInput?.value || "Work"
    
    // Calculate color based on category ID
    const colorIndex = categoryId ? (parseInt(categoryId) % categories.colors.length) : 0
    const color = categories.colors[colorIndex]
    
    return {
      name: categoryName,
      color: color
    }
  }
  
  calculateDurationFromForm(formData) {
    try {
      const startDate = formData.get('time_log[start_date]')
      const startTime = formData.get('time_log[start_time_only]')
      const endDate = formData.get('time_log[end_date]')
      const endTime = formData.get('time_log[end_time_only]')
      
      if (!startDate || !startTime || !endDate || !endTime) {
        // Try to calculate a simple duration as fallback
        return this.getFallbackDuration()
      }
      
      // Convert date formats to ISO format that JavaScript can parse
      // Handle different date formats: MM/DD/YYYY, DD/MM/YYYY, YYYY-MM-DD
      const normalizeDate = (dateStr) => {
        if (dateStr.includes('/')) {
          const parts = dateStr.split('/')
          if (parts.length === 3) {
            // Need to detect if it's MM/DD/YYYY or DD/MM/YYYY
            // Look at the first part - if it's > 12, it's DD/MM/YYYY
            const first = parseInt(parts[0])
            const second = parseInt(parts[1])
            
            if (first > 12) {
              // DD/MM/YYYY format
              const [day, month, year] = parts
              return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
            } else if (second > 12) {
              // MM/DD/YYYY format  
              const [month, day, year] = parts
              return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
            } else {
              // Ambiguous case - check if day looks reasonable for DD/MM format
              // Since your example shows 27/06/2025, let's default to DD/MM/YYYY
              const [day, month, year] = parts
              return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
            }
          }
        } else if (dateStr.includes('-')) {
          // Already in YYYY-MM-DD format
          return dateStr
        }
        return dateStr
      }
      
      const startDateISO = normalizeDate(startDate)
      const endDateISO = normalizeDate(endDate)
      
      // Create date objects with time
      const start = new Date(`${startDateISO}T${startTime}`)
      const end = new Date(`${endDateISO}T${endTime}`)
      
      // Check if dates are valid
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        return "New time log"
      }
      
      // Calculate difference in minutes
      const diffInMinutes = Math.floor((end - start) / (1000 * 60))
      
      if (diffInMinutes <= 0) {
        return "0m"
      }
      
      const hours = Math.floor(diffInMinutes / 60)
      const minutes = diffInMinutes % 60
      
      return hours > 0 ? (minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`) : `${minutes}m`
    } catch (error) {
      return this.getFallbackDuration()
    }
  }
  
  getFallbackDuration() {
    // Try to get duration from form fields directly
    const startTimeField = document.querySelector('input[name="time_log[start_time_only]"]')
    const endTimeField = document.querySelector('input[name="time_log[end_time_only]"]')
    
    if (startTimeField && endTimeField && startTimeField.value && endTimeField.value) {
      try {
        // Simple time calculation assuming same day
        const [startHours, startMinutes] = startTimeField.value.split(':').map(Number)
        const [endHours, endMinutes] = endTimeField.value.split(':').map(Number)
        
        const startTotalMinutes = startHours * 60 + startMinutes
        const endTotalMinutes = endHours * 60 + endMinutes
        
        let diffMinutes = endTotalMinutes - startTotalMinutes
        
        // Handle overnight case
        if (diffMinutes < 0) {
          diffMinutes += 24 * 60 // Add 24 hours
        }
        
        if (diffMinutes > 0) {
          const hours = Math.floor(diffMinutes / 60)
          const minutes = diffMinutes % 60
          
          if (hours > 0) {
            return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`
          } else {
            return `${minutes}m`
          }
        }
      } catch (error) {
        // Silently handle fallback calculation errors
      }
    }
    
    return "Time Log Created"
  }
  
  showCelebration(categoryInfo, duration) {
    // Remove any existing celebration
    const existing = document.getElementById('manual-log-celebration')
    if (existing) existing.remove()
    
    // Create celebration element
    const celebrationHtml = `
      <div id="manual-log-celebration" 
           class="fixed inset-0 pointer-events-none z-[10000] flex items-center justify-center"
           style="background-color: rgba(0, 0, 0, 0.1);">
        
        <div class="text-center">
          <div class="text-white px-12 py-8 rounded-2xl backdrop-blur-sm shadow-2xl border-2" 
               style="background: linear-gradient(135deg, ${categoryInfo.color}E6, ${categoryInfo.color}CC); border-color: ${categoryInfo.color}99; min-width: 280px;">
            
            <div class="text-center mb-6">
              <div class="text-6xl mb-4">${this.getRandomIcon()}</div>
              <div class="text-xs font-semibold opacity-70 tracking-wider uppercase border-b border-white border-opacity-20 pb-4 mb-5">
                Time Log Created
              </div>
            </div>
            
            <div class="text-lg font-medium opacity-95 mb-4">
              ${categoryInfo.name}
            </div>
            
            <div class="px-6 py-3 rounded-xl text-3xl font-mono font-bold mb-5 border border-white border-opacity-20" 
                 style="background: rgba(255, 255, 255, 0.3); backdrop-filter: blur(10px);">
              ${duration}
            </div>
            
            <div class="text-sm font-medium opacity-85">
              ${this.getRandomMessage()}
            </div>
          </div>
        </div>
      </div>
    `
    
    document.body.insertAdjacentHTML('beforeend', celebrationHtml)
    
    // Trigger confetti
    this.triggerConfetti(categoryInfo.color)
  }
  
  triggerConfetti(baseColor) {
    // Create color palette
    const colors = [
      baseColor,
      '#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FECA57', '#FF9FF3', '#54A0FF'
    ]
    
    // Initial burst
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: colors
    })
    
    // Side bursts
    setTimeout(() => {
      confetti({
        particleCount: 50,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.8 },
        colors: colors
      })
    }, 200)
    
    setTimeout(() => {
      confetti({
        particleCount: 50,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.8 },
        colors: colors
      })
    }, 400)
    
    // Additional celebration bursts
    setTimeout(() => {
      confetti({
        particleCount: 80,
        spread: 100,
        origin: { y: 0.2 },
        colors: colors
      })
    }, 800)
  }
  
  getRandomMessage() {
    const successMessages = [
      "Time Logged Successfully ✅",
      "Great Work Recorded 📝",
      "Activity Tracked ⏰",
      "Time Entry Complete 🎯",
      "Successfully Logged ✨",
      "Work Session Saved 💾",
      "Time Captured 📊",
      "Entry Added Successfully 🚀",
      "Logging Complete 👍",
      "Time Recorded ⭐"
    ]
    
    return successMessages[Math.floor(Math.random() * successMessages.length)]
  }
  
  getRandomIcon() {
    const logIcons = [
      "📝", "✅", "📊", "⏰", "🎯", "✨", "📋", "💾", "🚀", "⭐"
    ]
    
    return logIcons[Math.floor(Math.random() * logIcons.length)]
  }

  // Check if celebrations are enabled for the current user
  async isCelebrationsEnabled() {
    try {
      // Check if there's a way to get this from the page data first
      const celebrationsMetaTag = document.querySelector('meta[name="user-celebrations"]')
      if (celebrationsMetaTag) {
        return celebrationsMetaTag.content === 'true'
      }
      
      // Fallback: default to true for now
      return true
    } catch (error) {
      console.warn('Could not determine celebrations setting, defaulting to enabled:', error)
      return true
    }
  }
}